import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil.ts';
import { useMemo } from 'react';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices.ts';
import { ClassifierData } from 'types/classifiers';
import { RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { FeatureFlag } from 'types/features';
import {
    enrichDynamicFiltersWithPassiveFilter,
    filterMainFilters,
    filterQuickFilters,
    PaginatedFieldPassiveFilter,
} from 'utils/filterUtil.ts';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

// Add more if needed
export type FilterId = 'effortForPartyFilter' | 'myPortfolioEffortsFilter';
export type FilterGroup = 'efforts.filter-groups.contact';

/** The classifiers the BD efforts list reads, its filters included. */
export const EFFORT_LIST_CLASSIFIERS = ['EFFORT_ACTIVITY_TYPE', 'BUSINESS_AREA_TYPE', 'SEGMENT_TYPE'];

type Args = {
    classifiers: Record<string, ClassifierData>;
    passiveFilter?: string;
    disabledGroups?: FilterGroup[];
    disabledFilterIds?: FilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<FilterId>[];
};

/**
 * The BD efforts list's filters: its quick filters, the fields of its filter panel, what its search reads and the
 * filter it always applies. One definition for the list and for whatever else filters efforts by the same fields.
 */
export function useEffortFilterConfig({
    classifiers,
    passiveFilter = '',
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
}: Args): FilterConfig {
    const hasRankingsReferees = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE);
    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: filterQuickFilters<FilterId>(
                [
                    {
                        selector: 'effortByEmployee.id',
                        comperator: '=ismyeffort=',
                        filters: [
                            {
                                label: t('efforts.filter.my-efforts'),
                                tooltip: 'efforts.tooltips.my-efforts',
                                argument: 'true',
                            },
                        ],
                    },
                    {
                        id: 'myPortfolioEffortsFilter',
                        selector: 'effortByEmployee.id',
                        comperator: '=ismyportfolioeffort=',
                        filters: [
                            {
                                label: t('efforts.filter.my-portfolio-efforts'),
                                argument: 'true',
                                tooltip: 'efforts.tooltips.my-portfolio-efforts',
                            },
                        ],
                    },
                    {
                        selector: 'effortByEmployee.id',
                        comperator: '=ismycoreteameffort=',
                        filters: [
                            {
                                label: t('efforts.filter.my-core-team-bd-efforts'),
                                argument: 'true',
                                tooltip: 'efforts.tooltips.my-core-team-bd-efforts',
                            },
                        ],
                    },
                    {
                        selector: 'effortByEmployee.id',
                        comperator: '=ismyteameffort=',
                        filters: [
                            {
                                label: t('efforts.filter.my-team-bd-efforts'),
                                argument: 'true',
                                tooltip: 'efforts.tooltips.my-team-bd-efforts',
                            },
                        ],
                    },
                ],
                disabledFilterIds
            ),
            filters: enrichDynamicFiltersWithPassiveFilter(
                filterMainFilters(
                    [
                        // BD effort section
                        {
                            type: 'checkboxgroup',
                            selector: 'effortByEmployee.id',
                            comperator: '=ismyeffort=',
                            group: 'efforts.filter-groups.bd-effort',
                            filters: [
                                {
                                    label: t('efforts.filter.my-efforts'),
                                    tooltip: 'efforts.tooltips.my-efforts',
                                    argument: 'true',
                                },
                            ],
                        },
                        {
                            id: 'myPortfolioEffortsFilter',
                            type: 'checkboxgroup',
                            selector: 'effortByEmployee.id',
                            comperator: '=ismyportfolioeffort=',
                            group: 'efforts.filter-groups.bd-effort',
                            filters: [
                                {
                                    label: t('efforts.filter.my-portfolio-efforts'),
                                    argument: 'true',
                                    tooltip: 'efforts.tooltips.my-portfolio-efforts',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'effortByEmployee.id',
                            comperator: '=ismycoreteameffort=',
                            group: 'efforts.filter-groups.bd-effort',
                            filters: [
                                {
                                    label: t('efforts.filter.my-core-team-bd-efforts'),
                                    argument: 'true',
                                    tooltip: 'efforts.tooltips.my-core-team-bd-efforts',
                                },
                            ],
                        },
                        {
                            type: 'checkboxgroup',
                            selector: 'effortByEmployee.id',
                            comperator: '=ismyteameffort=',
                            group: 'efforts.filter-groups.bd-effort',
                            filters: [
                                {
                                    label: t('efforts.filter.my-team-bd-efforts'),
                                    argument: 'true',
                                    tooltip: 'efforts.tooltips.my-team-bd-efforts',
                                },
                            ],
                        },
                        {
                            type: 'multiselect',
                            label: t('efforts.activity-type'),
                            selector: 'effortActivityTypeCode',
                            group: 'efforts.filter-groups.bd-effort',
                            // Rankings referee is a type only with its feature.
                            options: classifiers
                                ? getFilterOptionsFromClassifier(classifiers?.['EFFORT_ACTIVITY_TYPE']).filter(
                                      ({ value }) => hasRankingsReferees || value !== RANKINGS_REFEREE_TYPE
                                  )
                                : [],
                        },
                        {
                            type: 'dynamicmultiselect',
                            label: t('efforts.effort-by'),
                            selector: 'effortByEmployee.id',
                            group: 'efforts.filter-groups.bd-effort',
                            tooltip: 'efforts.tooltips.effort-by',
                            comperator: '=iseffortby=',
                            paginated: {
                                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'dynamicmultiselect',
                            group: 'efforts.filter-groups.bd-effort',
                            label: t('efforts.filter.effort-by-office'),
                            selector: 'id',
                            comperator: '=iseffortbyoffice=',
                            tooltip: 'efforts.tooltips.effort-by-office',
                            paginated: {
                                fetch: useLazyGetMinOfficesWithFiltersQuery,
                                searchField: 'name',
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                            getOptionEndAdornment: officeInactiveSuffix,
                        },
                        {
                            type: 'daterange',
                            selector: 'effortDate',
                            group: 'efforts.filter-groups.bd-effort',
                            shouldDisplayQuickPick: true,
                        },
                        {
                            type: 'text',
                            selector: 'description',
                            label: t('efforts.filter.bd-effort-content-search'),
                            group: 'efforts.filter-groups.bd-effort',
                            tooltip: 'efforts.tooltips.bd-effort-content-search',
                        },
                        // Contact section
                        {
                            id: 'effortForPartyFilter',
                            type: 'dynamicmultiselect',
                            subType: 'contact',
                            label: t('efforts.filter.effort-for-contacts'),
                            selector: 'parties.party.id',
                            group: 'efforts.filter-groups.contact',
                            preserveValueWhenHidden: true,
                            paginated: {
                                fetch: useLazyGetMinContactsListWithFiltersQuery,
                                searchField: ['name', 'partyNumber'],
                                getOptionIdentifier: 'id',
                                getOptionLabel: 'name',
                            },
                        },
                        {
                            type: 'multiselect',
                            label: t('efforts.filter.effort-for-contact-withing-business-area'),
                            selector: 'id',
                            group: 'efforts.filter-groups.contact',
                            tooltip: 'efforts.tooltips.effort-for-contact-withing-business-area',
                            comperator: '=iseffortbybusinessarea=',
                            options: classifiers
                                ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE'])
                                : [],
                        },
                        {
                            type: 'multiselect',
                            label: t('efforts.filter.bd-effort-by-contact-segment'),
                            selector: 'id',
                            comperator: '=iseffortbysegment=',
                            group: 'efforts.filter-groups.contact',
                            tooltip: 'efforts.tooltips.bd-effort-by-contact-segment',
                            options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SEGMENT_TYPE']) : [],
                        },
                    ],
                    disabledGroups,
                    disabledFilterIds
                ),
                paginatedFieldPassiveFilters
            ),
            searchBarSelectors: [
                { selector: 'description' },
                { selector: 'effortActivityTypeCode' },
                { selector: 'effortByEmployee.fullName' },
                { selector: 'parties.party.name' },
            ],
            passiveFilter: passiveFilter,
        }),
        [
            classifiers,
            passiveFilter,
            disabledGroups,
            disabledFilterIds,
            paginatedFieldPassiveFilters,
            hasRankingsReferees,
        ]
    );

    return filterConfig;
}
